import React, { useState } from 'react';
import { calculateVoiceQuote } from '../utils/voicePricing';
import { downloadVoiceDocument } from '../utils/voiceDocuments';
export const yen = value => `${value.toLocaleString('ja-JP')}円`;
export const defaultVoiceOptions = { binaural: false, video: false, rate: 1.5, withholding: true };
export function VoiceOptions({ options, onChange, showWithholding = true }) {
  const update = (key, value) => onChange({ ...options, [key]: value });
  return <div className="voice-options">
    <label>1文字あたりの単価（円）<input type="number" required min="0.01" max="10000" step="0.01" value={options.rate ?? 1.5} onChange={e => update('rate', e.target.value)} /></label>
    <label><input type="checkbox" checked={options.binaural} onChange={e => update('binaural', e.target.checked)} /> バイノーラル収録 ＋1,000円</label>
    <label><input type="checkbox" checked={options.video} onChange={e => update('video', e.target.checked)} /> 朗読動画あり ＋5,000円（顔出しなしOK・体の撮影必須）</label>
    {showWithholding && <label><input type="checkbox" checked={options.withholding} onChange={e => update('withholding', e.target.checked)} /> 源泉徴収を適用する</label>}
  </div>;
}
export function VoiceSummary({ quote }) {
  return <div aria-live="polite"><dl className="jobs-details voice-summary">
    <div><dt>カウント対象</dt><dd>{quote.count.toLocaleString('ja-JP')}文字</dd></div>
    <div><dt>朗読料</dt><dd>{yen(quote.base)} <small>（1文字{quote.rate}円）</small></dd></div>
    <div><dt>バイノーラル</dt><dd>{yen(quote.extra)}</dd></div>
    <div><dt>朗読動画</dt><dd>{yen(quote.videoExtra)}</dd></div>
    <div><dt>報酬総額（税込）</dt><dd>{yen(quote.gross)}</dd></div>
    <div><dt>源泉徴収額</dt><dd>−{yen(quote.tax)}</dd></div>
    <div className="voice-total"><dt>差引受取額</dt><dd>{yen(quote.net)}</dd></div>
  </dl></div>;
}
export function VoiceRules() {
  return <details className="voice-rules"><summary>文字数・料金の計算ルール</summary>
    <p>漢字・ひらがな・カタカナ・長音「ー」をカウントします。丸括弧・角括弧など（ ） ( ) ［ ］ [ ] ｛ ｝ {'{ }'} 【 】 〔 〕 〈 〉 《 》の内側は、入れ子も含め除外します。「」『』内の会話本文は対象です。</p>
    <p>英字、数字、空白、改行、句読点、絵文字、その他の記号は除外します。閉じていない括弧は末尾まで除外するため、警告が出た場合は原稿を確認してください。</p>
    <p>朗読料は文字数 × 入力した単価（初期値1.5円）の円未満を切り捨て。オプション加算を含む税込総額で、消費税は別途加算しません。源泉徴収は総額の100万円以下の部分に10.21%、超過分に20.42%を適用し、税額の円未満を切り捨てます。国内居住の個人への報酬を想定しています。源泉徴収義務のない支払などはチェックを外してください。</p>
    <a href="https://www.nta.go.jp/taxes/shiraberu/taxanswer/gensen/2792_qa.htm" target="_blank" rel="noreferrer">計算の根拠：国税庁</a>
  </details>;
}
export default function VoiceCalculator({ initialText = '', initialTitle = '朗読業務', locked = false, options: sharedOptions, onOptionsChange }) {
  const [text, setText] = useState(initialText);
  const [localOptions, setLocalOptions] = useState(defaultVoiceOptions);
  const options = sharedOptions || localOptions;
  const setOptions = onOptionsChange || setLocalOptions;
  const [title, setTitle] = useState(initialTitle);
  const [recipient, setRecipient] = useState('ASMRキリスト教会');
  const [date, setDate] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; });
  const [reference, setReference] = useState('');
  let quote, error;
  try { quote = calculateVoiceQuote(text, options); } catch (e) { error = e.message; }
  const enabled = quote && quote.count > 0 && !quote.unmatched && title.trim() && recipient.trim() && date;
  const download = type => downloadVoiceDocument({ type, text, options, title, recipient, date, reference });
  return <div className="voice-form">
    <label htmlFor="voice-script">{locked ? '募集原稿' : '朗読原稿'}</label>
    <textarea id="voice-script" rows="12" value={text} readOnly={locked} onChange={e => setText(e.target.value)} placeholder="原稿を貼り付けてください。" />
    <VoiceRules />
    <VoiceOptions options={options} onChange={setOptions} />
    {error && <p role="alert">{error}</p>}
    {quote && <><VoiceSummary quote={quote} />{quote.unmatched && <p role="alert">対応しない括弧があります。原稿を確認してください。</p>}<details><summary>カウント対象の文字を確認</summary><p className="voice-counted">{quote.countedText || '対象の文字がありません。'}</p></details></>}
    <section className="jobs-section">
      <h2>請求書を作成</h2>
      <p>声優の氏名・住所・連絡先・振込先は空欄で出力します。HTMLファイルをダウンロードし、開いて印刷・PDF保存できます。原稿・書類情報はサーバーに送信しません。</p>
      <label>件名<input value={title} maxLength="200" onChange={e => setTitle(e.target.value)} /></label>
      <label>宛名<input value={recipient} maxLength="200" onChange={e => setRecipient(e.target.value)} /></label>
      <label>発行日<input type="date" value={date} onChange={e => setDate(e.target.value)} /></label>
      <label>書類番号（任意）<input value={reference} maxLength="80" onChange={e => setReference(e.target.value)} /></label>
      <div className="voice-actions"><button type="button" disabled={!enabled} onClick={() => download('invoice')}>請求書をダウンロード</button></div>
    </section>
  </div>;
}
